import { configure } from "vee-validate"
import { defineRule } from "vee-validate"
import { required, email, min, max, numeric, length, max_value } from "@vee-validate/rules"
import { isAfter } from "date-fns"
import { t } from "@/utils/i18n-utils"

export function setupVeeValidate() {
  // Configuration globale
  configure({
    generateMessage: (context) => {
      const { field, rule } = context

      // Gestion des messages par défaut
      if (rule?.name === "required") {
        return t("validation-input-invalid-required")
      }
      if (rule?.name === "email") {
        return t("validation-input-invalid-email")
      }

      // Message générique si aucun message spécifique n'est défini
      return `${field} n'est pas valide`
    },
    validateOnBlur: true,
    validateOnChange: true,
    validateOnInput: false,
    validateOnModelUpdate: true,
  })

  // Règles standards de vee-validate
  defineRule("required", (value: any) => {
    if (!required(value)) {
      return t("validation-input-invalid-required")
    }
    return true
  })

  // Règle spéciale pour les champs booléens (radio oui/non)
  // Accepte true et false, mais refuse null/undefined
  defineRule("required_boolean", (value: any) => {
    if (value === true || value === false) {
      return true
    }
    return t("validation-input-invalid-required")
  })

  defineRule("email", (value: any) => {
    if (!value) return true

    const asciiBeforeAt = /^[^@]*$/ // Checks for ASCII characters before the '@'
    const forbiddenChars = /['"&*`]/ // Disallow specific characters
    const accentedDomainChars = /[éàè]/ // Allow specific accented characters in the domain

    if (!value.includes("@")) return t("validation-input-invalid-email")

    const [localPart, domain] = value.split("@")

    // Validate only if there is a local part and a domain part
    if (localPart && domain) {
      return (
        (email(value) &&
          asciiBeforeAt.test(localPart) &&
          !accentedDomainChars.test(localPart) &&
          !forbiddenChars.test(localPart) &&
          (!forbiddenChars.test(domain) || !accentedDomainChars.test(domain))) ||
        t("validation-input-invalid-email")
      )
    }

    // If the email does not have two parts, it's invalid
    return t("validation-input-invalid-email")
  })

  defineRule("confirmed", (value: any, [target]: [string], ctx: any) => {
    if (value === ctx.form[target]) {
      return true
    }
    return t("validation-input-invalid-confirm-not-matching")
  })

  defineRule("validPassword", (value: any) => {
    if (!value) return true

    let validated = value && value.length >= 8
    validated = validated && /\d/.test(value)
    validated = validated && /[a-z]/.test(value)
    validated = validated && /[A-Z]/.test(value)

    if (validated) return true

    return t("validation-input-password-not-matching-regex")
  })

  defineRule("validPhoneNumber", (value: any) => {
    if (!value) return true

    let validated = value && value.length == 10
    validated = validated && /[0-9]{10}/.test(value)

    if (validated) return true

    return t("validation-input-phone-not-matching-regex")
  })

  defineRule("dateIsAfter", (value: any, [minDate]: [string]) => {
    if (!value || !minDate) return true

    if (isAfter(value, new Date(minDate))) {
      return true
    }

    return t("validation-input-date-not-after")
  })

  defineRule("checked", (value: any) => {
    if (value === true) return true
    return t("validation-checkbox-true")
  })

  defineRule("certified-exact", (value: any) => {
    if (value === true) return true
    return t("projet.inscription.finalisation-inscription.certified-error")
  })

  defineRule("accept-cookie", (value: any) => {
    if (value === true) return true
    return t("must-accept-cookie")
  })

  defineRule("accept-rgpd", (value: any) => {
    if (value === true) return true
    return t("registerPage.must-accept-rgpd")
  })

  defineRule("numeric", (value: any) => {
    if (!value) return true
    if (numeric(value)) return true
    return t("validation-input-invalid-number")
  })

  defineRule("postalCode", (value: any) => {
    if (!value) return true
    if (numeric(value)) return true
    return t("validation-input-invalid-number")
  })

  defineRule("validPostalCode", (value: any, [suggestedVillesLength]: [string]) => {
    if (!value) return true

    if (numeric(value) && parseInt(suggestedVillesLength) !== 0) {
      return true
    }

    return t("validation-input-invalid-postal-code")
  })

  defineRule("maxChoices", (value: any, [maxVal]: [string]) => {
    if (!value) return true

    if (value instanceof Array && value.length <= parseInt(maxVal)) {
      return true
    }

    return t("validation-max-choices")
  })

  defineRule("min", (value: any, [length]: [number]) => {
    if (!value) return true

    if (min(value, { length })) return true

    return t("validation-min-length").replace("{min}", length.toString())
  })

  defineRule("max", (value: any, [length]: [number]) => {
    if (!value) return true

    if (max(value, { length })) return true

    return t("validation-max-length").replace("{max}", length.toString())
  })

  defineRule("max_value", (value: any, [max]: [number]) => {
    if (!value) return true

    if (max_value(value, { max })) return true

    return t("validation-input-invalid-number")
  })

  defineRule("length", (value: any, [len]: [number]) => {
    if (!value) return true

    if (length(value, { length: len })) return true

    return t("validation-exact-length")
  })

  defineRule("shouldMatchFollowing", (value: any, [models]: [any]) => {
    if (!value) return true

    let array: any[] = models
    if (!Array.isArray(models)) {
      array = [models]
    }

    if (array && array.some((model: string) => model === value)) {
      return true
    }

    return t("validation-match-following")
  })

  defineRule("periodFinancementValid", (value: any) => {
    if (required(value)) return true
    return t("validation-financement-dates")
  })

  defineRule("niveauValid", (value: any) => {
    if (required(value)) return true
    return t("validation-classe-niveau-valid")
  })

  // Note: Importez et migrez ici les autres règles personnalisées
  // depuis l'ancien fichier vee-validation-rules.ts
}
